/**
 * Einwilligungs-Dialog für Google Analytics (nur nach Zustimmung wird gtag geladen).
 * Portal an document.body, Backdrop ohne Schließen per Klick, body scroll lock — kein <dialog>/Escape.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Alert, Box, Button, SpaceBetween } from '@cloudscape-design/components';
import { getStoredConsent, setStoredConsent, loadGtag } from '@/utils/analytics';

const PRIVACY_POLICY_HREF = 'https://www.svenesis.org/privacy/privacy_de.html';

const OVERLAY_Z = 200000;

interface AnalyticsConsentBannerProps {
  /** Wird aufgerufen, nachdem gtag geladen und konfiguriert ist (SPA-Pageviews aktivieren). */
  onGranted: () => void;
  /** Nach „Ablehnen“ oder Wechsel zu abgelehnt (z. B. Widerruf). */
  onDenied?: () => void;
  /** Bei jeder Erhöhung wird der Dialog geöffnet (Cookie-Einstellungen-Link). */
  openRequest?: number;
}

const AnalyticsConsentBanner: React.FC<AnalyticsConsentBannerProps> = ({
  onGranted,
  onDenied,
  openRequest = 0,
}) => {
  const [visible, setVisible] = useState(() => getStoredConsent() === null);
  const [busy, setBusy] = useState(false);
  const lastOpenRequestRef = useRef(0);

  useEffect(() => {
    if (openRequest > lastOpenRequestRef.current) {
      lastOpenRequestRef.current = openRequest;
      setVisible(true);
    }
  }, [openRequest]);

  const handleDecline = useCallback(() => {
    setStoredConsent('denied');
    setVisible(false);
    onDenied?.();
  }, [onDenied]);

  const handleAccept = useCallback(async () => {
    setBusy(true);
    try {
      await loadGtag();
    } catch {
      // Safari ITP oder Content-Blocker kann gtag blockieren —
      // Consent trotzdem speichern, da der Nutzer zugestimmt hat.
      // Bei nächster Navigation wird loadGtag erneut versucht.
      console.warn('[Analytics] gtag.js konnte nicht geladen werden (evtl. Tracking-Schutz im Browser).');
    }
    // Consent immer speichern und Dialog schließen, unabhängig davon ob gtag laden konnte.
    setStoredConsent('granted');
    setVisible(false);
    setBusy(false);
    onGranted();
  }, [onGranted]);

  useEffect(() => {
    if (!visible) return;
    setBusy(false);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [visible]);

  if (!visible) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: OVERLAY_Z,
        isolation: 'isolate',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'clamp(16px, 4vw, 24px)',
        boxSizing: 'border-box',
      }}
    >
      {/* Backdrop: keine Interaktion zum Schließen */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.55)',
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="analytics-consent-dialog-title"
        id="analytics-consent-dialog"
        style={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: '720px',
          pointerEvents: 'auto',
          boxShadow: '0 8px 32px rgba(15, 23, 42, 0.2)',
        }}
      >
        <Alert
          type="info"
          header={
            <span id="analytics-consent-dialog-title">Nutzungsanalyse (optional)</span>
          }
          action={
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={handleDecline} disabled={busy}>
                Ablehnen
              </Button>
              <Button variant="primary" onClick={handleAccept} loading={busy}>
                Akzeptieren
              </Button>
            </SpaceBetween>
          }
        >
          <Box variant="p">
            Ich setze <strong>Google Analytics 4</strong> ein, um zu verstehen, wie diese App genutzt wird
            (z.&nbsp;B. aufgerufene Bereiche und grobe Nutzungswege). Die Auswertung erfolgt mit
            pseudonymen Kennungen; es werden <strong>keine</strong> Inhalte aus Parqet, <strong>keine</strong>{' '}
            Portfolio- oder Steuerdaten und <strong>keine</strong> Finanzinformationen aus der App an Google
            übermittelt.
          </Box>
          <Box variant="p" margin={{ top: 's' }}>
            Rechtsgrundlage ist Ihre <strong>Einwilligung</strong> (Art.&nbsp;6 Abs.&nbsp;1 lit.&nbsp;a DSGVO
            i.&nbsp;V.&nbsp;m. §&nbsp;25 Abs.&nbsp;1 TTDSG). Die Einwilligung ist freiwillig; bei Ablehnung
            wird kein Analyse-Skript geladen. Sie können sie jederzeit mit Wirkung für die Zukunft über{' '}
            <strong>Cookie-Einstellungen</strong> im Fußbereich der App widerrufen.
          </Box>
          <Box variant="p" margin={{ top: 's' }}>
            Anbieter ist die Google Ireland Limited; je nach Verarbeitung kann eine Übermittlung in die USA
            erfolgen. Einzelheiten zu Zweck, Speicherdauer und Ihren Rechten finden Sie unter{' '}
            <a href={PRIVACY_POLICY_HREF} target="_blank" rel="noopener noreferrer">
              Datenschutz
            </a>
            .
          </Box>
        </Alert>
      </div>
    </div>,
    document.body
  );
};

export default AnalyticsConsentBanner;
